import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { LiveTileContent } from "../../LiveTileContent";
import {
	GeneralIntegrationFlowConfiguration,
	GeneralIntegrationFlowResult,
	GeneralIntegrationFlowResultItemModel,
	GeneralIntegrationFlowLiveInformationResultStatus,
	getFlowConfiguration,
	getGeneralLiveInformation
} from "client/Api/Workspace/LiveInformation/Integration/integrationLiveInformation";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { never } from "client_legacy/ndaw/errors";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	BusyIndicator,
	ChangeTriggerKind,
	DateString,
	ListItem,
	SearchInputTrigger
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { generateTrackingId, isNotNilOrWhiteSpace } from "client/Misc/utils";
import styles from "./GeneralLiveTileContent.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { ResultMapperColor } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { useForceRerender } from "client/Hooks";
import { ColoredText } from "../ColoredText";
import { Subtitles } from "../Subtitles";
import { AuthenticationRequired } from "../AuthenticationRequired";

export type GeneralLiveTileContentProps = {
	connectionId: number;
	appName: string;
	userTimezone: TimeZone;
};

export const GeneralLiveTileContent = (props: GeneralLiveTileContentProps): ReactElement => {
	const {
		connectionId,
		appName,
		userTimezone
	} = props;

	const [state, setState] = useState<State>({
		status: "isLoading"
	});

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [configuration, setConfiguration] = useState<ConfigurationState>({
		status: "isLoading"
	});

	const [reload, reloadVersion] = useForceRerender();

	const hasSearchTerm: boolean = isNotNilOrWhiteSpace(searchTerm.value);

	const doesSupportSearch: boolean = configuration.status === "loaded"
		&& configuration.doesSupportSearch;

	const searchTermChangeHandler = useCallback(
		(newValue: string, version: Guid): void => {
			setSearchTerm({
				value: newValue,
				version
			});
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(reloadVersion);

			setConfiguration({
				status: "isLoading"
			});

			const cancellation = new AbortController();

			const load = async (): Promise<void> => {
				try {
					const result: GeneralIntegrationFlowConfiguration = await getFlowConfiguration(
						connectionId,
						cancellation.signal
					);

					setConfiguration({
						status: "loaded",
						doesSupportSearch: result.doesSupportSearch
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setConfiguration({
						status: "failed"
					});
				}
			};

			load();

			return (): void => {
				cancellation.abort();
			};
		},
		[
			reloadVersion,
			connectionId
		]
	);

	useEffect(
		(): (() => void) | undefined => {
			if (configuration.status !== "loaded") {
				return undefined;
			}

			if (configuration.doesSupportSearch && !hasSearchTerm) {
				return undefined;
			}

			setState({
				status: "isLoading"
			});

			const cancellation = new AbortController();

			const load = async (): Promise<void> => {
				try {
					const result: GeneralIntegrationFlowResult = await getGeneralLiveInformation(
						connectionId,
						{
							searchTerm: hasSearchTerm
								? {
									isSet: true,
									value: searchTerm.value
								}
								: { isSet: false }
						},
						cancellation.signal
					);

					switch (result.status) {
						case GeneralIntegrationFlowLiveInformationResultStatus.Failure:
							setState({
								status: "globalFailure"
							});
							break;
						case GeneralIntegrationFlowLiveInformationResultStatus.AuthenticationIsRequired:
							setState({
								status: "authenticationRequired",
								authenticationModel: result.authenticationModel
							});
							break;
						case GeneralIntegrationFlowLiveInformationResultStatus.Success:
							setState({
								status: "loaded",
								items: result.items.map(
									x => ({
										...x,
										uniqueId: newGuid()
									})
								)
							});
							break;
						default:
							never(result);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setState({
						status: "globalFailure"
					});
				}
			};

			load();

			return (): void => {
				cancellation.abort();
			};
		},
		[
			searchTerm,
			hasSearchTerm,
			configuration,
			connectionId
		]
	);

	const renderGlobalFailure = (): ReactElement => (
		<LiveTileContent.NoResults
			icon="gear"
			title={localize("_UnableToRetrieveItems")}
			message={localize("_WeWereUnableToRetrieveTheItems")}
			onRetry={reload}
			trackingId="global-failure"
		/>
	);

	const renderFlowContent = (): ReactElement => {
		if (doesSupportSearch && !hasSearchTerm) {
			return (
				<LiveTileContent.NoResults
					icon="magnifying-glass"
					title={localize("_EnterASearchTerm")}
					message={localize("_PleaseEnterASearchTermToBeginYourSearch")}
					trackingId="search-term-required"
				/>
			);
		}

		switch (state.status) {
			case "globalFailure":
				return renderGlobalFailure();
			case "isLoading":
				return (<BusyIndicator />);
			case "authenticationRequired":
				return (
					<AuthenticationRequired
						appName={appName}
						authenticationModel={state.authenticationModel}
						onAuthenticated={reload}
					/>
				);
			case "loaded":
				if (state.items.length === 0) {
					if (hasSearchTerm) {
						return (
							<LiveTileContent.NoResults
								icon="magnifying-glass"
								title={localize("_NoSearchResults")}
								message={localize("_PleaseTrySearchingWithAnotherTerm")}
								trackingId="no-search-results"
							/>
						);
					}

					return (
						<LiveTileContent.NoResults
							icon="list"
							title={localize("_NoItemsToShow")}
							message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
							trackingId="no-items"
						/>
					);
				}

				return (
					<LiveTileContent.Scrollable>
						<List
							items={state.items}
							userTimezone={userTimezone}
						/>
					</LiveTileContent.Scrollable>
				);
			default:
				return never(state);
		}
	};

	const renderContent = (): ReactElement => {
		switch (configuration.status) {
			case "isLoading":
				return (<BusyIndicator />);
			case "failed":
				return renderGlobalFailure();
			case "loaded":
				return renderFlowContent();
			default:
				return never(configuration);
		}
	};

	return (
		<LiveTileContent.ContentStack>
			{doesSupportSearch && (
				<LiveTileContent.SearchInput
					value={searchTerm}
					onChange={searchTermChangeHandler}
					trigger={itemSearchTrigger}
					trackingId="search-input"
					placeholder={localize("_Search")}
				/>
			)}

			<LiveTileContent.BusyIndicatorContainer>
				{renderContent()}
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
GeneralLiveTileContent.displayName = "GeneralLiveTileContent";

const itemSearchTrigger: SearchInputTrigger = { kind: ChangeTriggerKind.Debounced };

type ConfigurationState = {
	status: "isLoading" | "failed";
} | {
	status: "loaded";
	doesSupportSearch: boolean;
};

type State = {
	status: "globalFailure" | "isLoading";
} | {
	status: "authenticationRequired";
	authenticationModel: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
} | {
	status: "loaded";
	items: readonly IntegrationTileItemModel[];
};

type IntegrationTileItemModel = GeneralIntegrationFlowResultItemModel & {
	uniqueId: Guid;
};

type ListProps = {
	items: readonly IntegrationTileItemModel[];
	userTimezone: TimeZone;
};

const List = (props: ListProps): ReactElement => {
	const {
		items,
		userTimezone
	} = props;

	return (
		<LiveTileContent.List>
			{items.map(item => (
				<li key={item.uniqueId}>
					<Item
						item={item}
						userTimezone={userTimezone}
					/>
				</li>
			))}
		</LiveTileContent.List>
	);
};
List.displayName = "List";

type ItemProps = {
	item: IntegrationTileItemModel;
	userTimezone: TimeZone;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		userTimezone
	} = props;

	const {
		title,
		dateTime,
		subtitle1,
		subtitle2,
		subtitle3,
		description
	} = item;

	const titleValue: string | undefined = title?.value;

	const computedTitle: string = isNotNilOrWhiteSpace(titleValue)
		? titleValue
		: localize("_NoTitle");

	return (
		<ListItem
			trackingId={generateTrackingId("integration-live-tile-item", `|${item.uniqueId}`)}
		>
			<ListItem.Content
				{...item.link
					? {
						href: item.link,
						isTargetBlank: true,
					}
					: {}
				}
				tooltip={titleValue}
				thumbnail={
					dateTime
						? (
							<Thumbnail
								dateTime={dateTime.value}
								color={dateTime.color}
								userTimezone={userTimezone}
							/>
						)
						: undefined
				}
			>
				<ListItem.Title
					tooltip={computedTitle}
				>
					<ColoredText
						trackingId="title-text"
						color={title?.color}
					>
						{computedTitle}
					</ColoredText>
				</ListItem.Title>
				<Subtitles
					subTitle1={subtitle1}
					subTitle2={subtitle2}
					subTitle3={subtitle3}
				/>
				{description?.value && (
					<div className={styles.description}>
						<ColoredText
							trackingId="description"
							color={description.color}
						>
							{description.value}
						</ColoredText>
					</div>
				)}
			</ListItem.Content>
		</ListItem>
	);
};
Item.displayName = "Item";

type ThumbnailProps = {
	dateTime: string;
	color: ResultMapperColor | null;
	userTimezone: TimeZone;
};

const Thumbnail = (props: ThumbnailProps): ReactElement => {
	const {
		dateTime,
		color,
		userTimezone
	} = props;

	return (
		<div
			className={styles.thumbnail}
		>
			<h4>
				<ColoredText
					trackingId="thumbnail-date"
					color={color}
				>
					<DateString
						value={dateTime}
						format="DM"
						timezone={userTimezone}
					/>
				</ColoredText>
			</h4>
			<div className={styles.thumbnailTime}>
				<ColoredText
					trackingId="thumbnail-time"
					color={color}
				>
					<DateString
						value={dateTime}
						format="T"
						timezone={userTimezone}
					/>
				</ColoredText>
			</div>
		</div>
	);
};
Thumbnail.displayName = "Thumbnail";
